import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings, Shield, Globe, LogOut, Pencil, ChevronRight, Save, X, Loader2,
  Palette, Grid3x3, Rows3, Check, PlayCircle, Wrench, Cpu, Plus, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useTheme } from "../theme/ThemeContext";
import { useAuth } from "../auth/AuthContext";
import { Card, StatusDot, Chip, PageHeader, Button, Label, Input, Textarea } from "../components/Primitives";
import { updateProfile, updateVisibility } from "../lib/db";
import { PROCESSES, MATERIALS, EQUIPMENT_PRESETS } from "../data/taxonomy";
import { clearOnboarding } from "./Onboarding";

function OptionRow({ label, testid, children }) {
  return (
    <div className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="flex-1 min-w-0 text-sm">{label}</div>
      <div className="flex gap-1 p-1 bg-carbon-900 rounded-md border border-carbon-600">
        {children}
      </div>
    </div>
  );
}

function PillButton({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1 text-xs rounded transition ${
        active ? "bg-electric-500 text-white shadow-[0_2px_8px_-2px_rgb(var(--e-500)/0.5)]" : "text-ink-700 hover:text-ink-900"
      }`}
    >
      {children}
    </button>
  );
}

/* ────────── Capabilities editor — capability-based matching (P0) ────────── */
function TogglePill({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
        active ? "border-electric-500 bg-electric-500/10 text-electric-600"
               : "border-carbon-600 text-ink-700 hover:text-ink-900"
      }`}>
      {children}
    </button>
  );
}

function CapabilitiesEditor({ profile, onSaved }) {
  const { t } = useI18n();
  const [processes, setProcesses] = React.useState([]);
  const [materials, setMaterials] = React.useState([]);
  const [equipment, setEquipment] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    if (dirty || saving) return;
    setProcesses(profile?.processes || []);
    setMaterials(profile?.materials || []);
    setEquipment(profile?.equipment || []);
    setDirty(false);
  }, [profile?.id]);

  const toggle = (list, setList, item) => {
    const has = list.map((x) => x.toLowerCase()).includes(item.toLowerCase());
    setList(has ? list.filter((x) => x.toLowerCase() !== item.toLowerCase()) : [...list, item]);
    setDirty(true);
  };
  const addEquipment = (preset) => {
    setEquipment([...equipment, { name: preset?.name || "", process: preset?.process || "", capacity: "" }]);
    setDirty(true); setExpanded(true);
  };
  const updateEquipment = (i, patch) => {
    setEquipment(equipment.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
    setDirty(true);
  };
  const removeEquipment = (i) => { setEquipment(equipment.filter((_, idx) => idx !== i)); setDirty(true); };

  const save = async () => {
    if (!profile?.id) {
      toast.error("Profile not loaded — please refresh");
      return;
    }
    setSaving(true);
    try {
      // Send ONLY capability fields so other profile columns are never overwritten.
      const cleanEquipment = (equipment || []).map((e) => ({
        name: (e?.name || "").trim(),
        process: (e?.process || "").trim(),
        capacity: (e?.capacity || "").trim(),
      }));
      const updated = await updateProfile(profile.id, {
        processes: processes || [],
        materials: materials || [],
        equipment: cleanEquipment,
      });
      // Re-hydrate local state from the DB truth so what we show == what was saved.
      setProcesses(updated?.processes || []);
      setMaterials(updated?.materials || []);
      setEquipment(updated?.equipment || []);
      await onSaved?.();
      toast.success(t("capabilities.saved"));
      setDirty(false);
    } catch (e) {
      // Surface the real Supabase error so silent persistence failures are visible.
      const msg = e?.message || e?.error_description || e?.details || "Could not save";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const decl = (processes.length + materials.length + equipment.length);

  return (
    <section className="mb-6" data-testid="capabilities-section">
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1 flex items-center gap-1.5">
        <Cpu className="w-3 h-3" /> {t("capabilities.title")}
      </div>
      <p className="text-[11px] text-ink-500 mb-3 px-1">{t("capabilities.subtitle")}</p>

      <Card className="p-5 space-y-5">
        {/* Processes */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="!mb-0">{t("capabilities.processes")}</Label>
            <span className="text-[10px] font-mono text-ink-500">{processes.length}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PROCESSES.map((p) => (
              <TogglePill key={p} active={processes.includes(p)} onClick={() => toggle(processes, setProcesses, p)} testid={`cap-proc-${p}`}>
                {p}
              </TogglePill>
            ))}
          </div>
        </div>

        {/* Materials */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="!mb-0">{t("capabilities.materials")}</Label>
            <span className="text-[10px] font-mono text-ink-500">{materials.length}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {MATERIALS.map((m) => (
              <TogglePill key={m} active={materials.includes(m)} onClick={() => toggle(materials, setMaterials, m)} testid={`cap-mat-${m}`}>
                {m}
              </TogglePill>
            ))}
          </div>
        </div>

        {/* Equipment */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="!mb-0 flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> {t("capabilities.equipment")}</Label>
            <button onClick={() => setExpanded((v) => !v)} className="text-[11px] text-ink-600 hover:text-ink-900" data-testid="cap-equipment-toggle">
              {expanded ? t("common.hide") : t("common.show")} ({equipment.length})
            </button>
          </div>
          {expanded && (
            <div className="space-y-2" data-testid="cap-equipment-list">
              {equipment.map((e, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.2fr_1.2fr_1fr_auto] gap-2 items-center p-2 rounded-md bg-carbon-750/60 border border-carbon-600">
                  <Input value={e.name || ""} onChange={(ev) => updateEquipment(i, { name: ev.target.value })}
                    placeholder={t("capabilities.equipmentName")} data-testid={`cap-eq-name-${i}`} />
                  <select value={e.process || ""} onChange={(ev) => updateEquipment(i, { process: ev.target.value })}
                    data-testid={`cap-eq-process-${i}`}
                    className="w-full px-3 py-2 rounded-md bg-carbon-800 border border-carbon-600 text-sm text-ink-900 focus:border-electric-500 focus:outline-none">
                    <option value="">{t("capabilities.equipmentProcess")}</option>
                    {PROCESSES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <Input value={e.capacity || ""} onChange={(ev) => updateEquipment(i, { capacity: ev.target.value })}
                    placeholder={t("capabilities.equipmentCapacity")} data-testid={`cap-eq-cap-${i}`} />
                  <button onClick={() => removeEquipment(i)} className="p-2 rounded-md text-ink-600 hover:text-red-400" data-testid={`cap-eq-rm-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex flex-wrap gap-1.5 pt-2">
                <button onClick={() => addEquipment(null)} data-testid="cap-eq-add-blank"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border border-electric-500/40 text-electric-600 hover:bg-electric-500/10">
                  <Plus className="w-3 h-3" /> {t("capabilities.addEquipment")}
                </button>
                {EQUIPMENT_PRESETS.slice(0, 8).map((preset) => (
                  <button key={preset.name} onClick={() => addEquipment(preset)}
                    data-testid={`cap-eq-preset-${preset.name}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border border-carbon-600 text-ink-700 hover:text-ink-900">
                    <Plus className="w-3 h-3" /> {preset.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-carbon-600 flex items-center gap-3">
          <div className="flex-1 text-[11px] text-ink-500">
            {decl === 0 ? t("capabilities.emptyHelp") : `${processes.length} • ${materials.length} • ${equipment.length}`}
          </div>
          <Button onClick={save} disabled={saving || !dirty || !profile?.id} data-testid="capabilities-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t("common.save")}
          </Button>
        </div>
      </Card>
    </section>
  );
}


/* ────────── Visibility editor — Profile Visibility V1 ────────── */
function VisibilityEditor({ profile, onSaved }) {
  const { t } = useI18n();
  const [value, setValue] = React.useState(profile?.visibility || "open");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { setValue(profile?.visibility || "open"); }, [profile?.id, profile?.visibility]);

  const choose = async (next) => {
    if (!profile?.id || next === value || saving) return;
    const prev = value;
    setValue(next);
    setSaving(true);
    try {
      await updateVisibility(profile.id, next);
      await onSaved?.();
      toast.success(t("visibility.saved"));
    } catch (e) {
      setValue(prev);
      toast.error(e?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const Option = ({ id, title, help }) => {
    const active = value === id;
    return (
      <button onClick={() => choose(id)} data-testid={`visibility-option-${id}`} disabled={saving}
        className={`w-full text-left p-4 rounded-xl border transition ${
          active
            ? "border-electric-500 bg-electric-500/10"
            : "border-carbon-600 hover:border-carbon-500 bg-carbon-800/60"
        }`}>
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
            active ? "border-electric-500 bg-electric-500" : "border-carbon-500"
          }`}>
            {active && <Check className="w-2.5 h-2.5 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-ink-900">{title}</div>
            <div className="text-[11px] text-ink-500 mt-1 leading-relaxed">{help}</div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <section className="mb-6" data-testid="visibility-section">
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1 flex items-center gap-1.5">
        <Shield className="w-3 h-3" /> {t("visibility.title")}
      </div>
      <p className="text-[11px] text-ink-500 mb-3 px-1">{t("visibility.subtitle")}</p>
      <Card className="p-4 space-y-2">
        <Option id="open" title={t("visibility.open")} help={t("visibility.openHelp")} />
        <Option id="private" title={t("visibility.private")} help={t("visibility.privateHelp")} />
      </Card>
    </section>
  );
}


function Row({ icon: Icon, label, value, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 p-4 hover:bg-carbon-750 transition text-left">
      <div className="w-9 h-9 rounded-lg bg-carbon-750 border border-carbon-600 flex items-center justify-center text-ink-600">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink-900">{label}</div>
        {value && <div className="text-[11px] text-ink-500 mt-0.5">{value}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-ink-400" />
    </button>
  );
}

export default function Profile() {
  const { t, lang, setLanguage } = useI18n();
  const { theme, blueprint, density, setTheme, setBlueprint, setDensity, themes, blueprints, densities } = useTheme();
  const { user, profile, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    positioning: "",
    location: "",
    bio: "",
    skills: "",
    materials: "",
  });

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || "",
        positioning: profile.positioning || "",
        location: profile.location || "",
        bio: profile.bio || "",
        skills: (profile.skills || []).join(", "),
        materials: (profile.materials || []).join(", "),
      });
    }
  }, [profile]);

  const displayName = profile?.full_name || user?.email?.split("@")[0] || "You";
  const displayCompany = profile?.account_type === "ORGANIZATION"
    ? (profile?.positioning || "Organization")
    : (profile?.positioning || "Person");
  const initials = (displayName || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "R4";

  const saveEdits = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      await updateProfile(user.id, {
        full_name: form.full_name || null,
        positioning: form.positioning || null,
        location: form.location || null,
        bio: form.bio || null,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        materials: form.materials.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await refreshProfile();
      toast.success("Profile updated");
      setEditing(false);
    } catch (e) {
      toast.error(e?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const doSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto animate-fade-up">
      <PageHeader title={t("profile.title")} testId="profile-header" />

      <Card className="p-5 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-electric-500 to-electric-600 flex items-center justify-center font-mono text-xl font-bold text-white">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-semibold text-ink-900">{displayName}</div>
            <div className="text-sm text-ink-600 truncate">{displayCompany}</div>
            <div className="flex items-center gap-2 mt-1.5 text-[11px] text-ink-500">
              <StatusDot status={profile?.availability || "available"} />
              <span>{t(`availability.${profile?.availability || "available"}`)}</span>
              {user?.email && (<><span>·</span><span className="font-mono">{user.email}</span></>)}
            </div>
          </div>
          {!editing ? (
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="profile-edit">
              <Pencil className="w-4 h-4" />
              {t("profile.edit")}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="profile-edit-cancel">
                <X className="w-4 h-4" />
                {t("common.cancel")}
              </Button>
              <Button onClick={saveEdits} disabled={saving} data-testid="profile-edit-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t("common.save")}
              </Button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="mt-5 pt-5 border-t border-carbon-600 space-y-3 animate-fade-up">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Full name</Label>
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="edit-full-name" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="edit-location" />
              </div>
            </div>
            <div>
              <Label>Positioning (one-liner)</Label>
              <Input value={form.positioning} onChange={(e) => setForm({ ...form, positioning: e.target.value })} data-testid="edit-positioning" />
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="edit-bio" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Skills (comma-separated)</Label>
                <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="CNC, Laser cutting" data-testid="edit-skills" />
              </div>
              <div>
                <Label>Materials (comma-separated)</Label>
                <Input value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} placeholder="Aluminum, Steel" data-testid="edit-materials" />
              </div>
            </div>
          </div>
        ) : (profile?.skills?.length || profile?.materials?.length) ? (
          <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-carbon-600">
            {(profile?.skills || []).slice(0, 6).map((s) => <Chip key={`s-${s}`}>{s}</Chip>)}
            {(profile?.materials || []).slice(0, 4).map((m) => <Chip key={`m-${m}`}>{m}</Chip>)}
          </div>
        ) : null}
      </Card>

      <CapabilitiesEditor profile={profile} onSaved={async () => { await refreshProfile(); }} />

      <VisibilityEditor profile={profile} onSaved={async () => { await refreshProfile(); }} />

      <div className="space-y-6">
        <section>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1 flex items-center gap-1.5">
            <Palette className="w-3 h-3" /> {t("profile.appearance")}
          </div>
          <p className="text-[11px] text-ink-500 mb-3 px-1">{t("profile.appearanceSub")}</p>

          <Card className="p-4 space-y-4" data-testid="theme-studio">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2">
                {t("profile.themeLabel")}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {themes.map((k) => {
                  const active = theme === k;
                  const swatches = {
                    warm:       ["#F7F3E9", "#FFFFFF", "#D9CFB2"],
                    terminal:   ["#080D0B", "#101A16", "#26B28C"],
                    industrial: ["#0B0907", "#16120E", "#E87422"],
                    technical:  ["#FFFFFF", "#F6F6F4", "#E87422"],
                    dark:       ["#0F1319", "#1A2029", "#33404F"],
                    light:      ["#F7F8FA", "#FFFFFF", "#D0D5DD"],
                  }[k];
                  return (
                    <button key={k} onClick={() => setTheme(k)} data-testid={`theme-option-${k}`}
                      className={`relative text-left p-3 rounded-xl border transition ${
                        active
                          ? "border-electric-500 bg-electric-500/10"
                          : "border-carbon-600 hover:border-carbon-500 bg-carbon-800"
                      }`}
                    >
                      {active && (
                        <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-electric-500 text-white flex items-center justify-center">
                          <Check className="w-2.5 h-2.5" />
                        </span>
                      )}
                      <div className="flex gap-1 mb-2">
                        {swatches.map((c, i) => (
                          <span key={i} className="w-4 h-4 rounded" style={{ background: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }} />
                        ))}
                      </div>
                      <div className="text-xs font-medium text-ink-900">{t(`profile.themes.${k}`)}</div>
                      <div className="text-[10px] text-ink-500 mt-0.5 leading-snug">{t(`profile.themeDesc.${k}`)}</div>
                      {k === "warm" && !active && (
                        <div className="mt-1.5 inline-block text-[9px] font-mono uppercase tracking-widest text-electric-600">
                          {t("profile.recommended")}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <OptionRow
              label={<span className="flex items-center gap-2"><Grid3x3 className="w-4 h-4 text-ink-500" /> {t("profile.blueprintLabel")}</span>}
              testid="blueprint-row"
            >
              {blueprints.map((b) => (
                <PillButton key={b} active={blueprint === b} onClick={() => setBlueprint(b)} testid={`blueprint-${b}`}>
                  {t(`profile.blueprint.${b}`)}
                </PillButton>
              ))}
            </OptionRow>

            <OptionRow
              label={<span className="flex items-center gap-2"><Rows3 className="w-4 h-4 text-ink-500" /> {t("profile.densityLabel")}</span>}
              testid="density-row"
            >
              {densities.map((d) => (
                <PillButton key={d} active={density === d} onClick={() => setDensity(d)} testid={`density-${d}`}>
                  {t(`profile.density.${d}`)}
                </PillButton>
              ))}
            </OptionRow>
          </Card>
        </section>

        <section>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1">
            {t("profile.preferences")}
          </div>
          <Card className="divide-y divide-carbon-600">
            <OptionRow
              label={<span className="flex items-center gap-2"><Globe className="w-4 h-4 text-ink-500" /> {t("profile.language")}</span>}
              testid="lang-row"
            >
              {["it", "en"].map((l) => (
                <PillButton key={l} active={lang === l} onClick={() => setLanguage(l)} testid={`profile-lang-${l}`}>
                  <span className="font-mono uppercase tracking-widest">{l}</span>
                </PillButton>
              ))}
            </OptionRow>
            <Row
              icon={PlayCircle}
              label={t("onboarding.review")}
              onClick={() => { clearOnboarding(); navigate("/app/onboarding"); }}
              testid="profile-review-onboarding"
            />
          </Card>
        </section>

        <section>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1">
            {t("profile.account")}
          </div>
          <Card className="divide-y divide-carbon-600">
            <Row icon={Settings} label={t("profile.account")} value={user?.email || ""} onClick={() => navigate("/app/account")} testid="profile-account" />
            <Row icon={Shield} label={t("profile.security")} value="2FA" onClick={() => navigate("/app/security")} testid="profile-security" />
          </Card>
        </section>

        <button onClick={doSignOut} data-testid="profile-logout"
          className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-carbon-600 text-ink-600 hover:text-ink-900 hover:border-electric-500/40 transition"
        >
          <LogOut className="w-4 h-4" />
          {t("profile.logout")}
        </button>
      </div>
    </div>
  );
}
